<script setup lang="ts">
import { $getChangeRequest, $getChangeRequestOptions, $updateChangeRequest } from '@/api/changeRequests'
import { useSnackbarStore } from '@/store/snackbar'
import { isDateOverdue } from '@/helpers/utils/isDateOverdue'
import { getCleanFilenameFromUrl } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { getFileIcon } from '@/helpers/utils/files/getFileIcon'
import { requiredValidator } from '@validators'
import { CR_CANCELLED_STATUS_VALUE } from '@/helpers/constants/changeRequests'

import type {
  ChangeRequestTableOptions,
  ChangeRequest,
  ChangeRequestTableSorting,
  ChangeRequestType,
  ChangeRequestProjectType,
  ChangeRequestServicesRequired,
  CampaignSelect as CampaignSelectInterface,
  ChangeRequestStatus,
  ChangeRequestPriority,
  FileWithReference,
  ChangeRequestReasonForChange,
  ChangeRequestCategory2
} from '@/models/changeRequest'
import { usePriority } from '@/helpers/composables/usePriority'

interface IProps {
  changeRequestId: number | string | null | undefined
  actionRequiredFilter?: boolean,
  showChangeRequestExternalLink?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  changeRequestId: null,
  actionRequiredFilter: false,
  showChangeRequestExternalLink: false,
})

interface CustomEvents {
  (e: 'onChangeRequestUpdate'): void
  (e: 'accessLoaded', value: boolean): void
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const {
  getPriorityColor
} = usePriority()

const route = useRoute()
const router = useRouter()

const isMounted = ref<boolean>(false)

const originalChangeRequest = ref<ChangeRequest>({})
let recordLoadVersion = 0
const hasOrdinaryAccess = computed(() => originalChangeRequest.value.has_ordinary_access === true)
const changeRequestLoading = ref<boolean>(false)

const changeRequestForm = ref()

const isCrEditMode = ref<boolean>(false)
const changeRequest = ref<ChangeRequest>({})
const originalPriority = ref<number | null>(null)
const originalSupplierIds = ref<number[]>([])
const originalStatus = ref<number | null>(null)
const showPriorityCascadeDialog = ref<boolean>(false)
const showSupplierCascadeDialog = ref<boolean>(false)
const showCancelledStatusCascadeDialog = ref<boolean>(false)
const pendingApplyPriorityToChildren = ref<boolean | undefined>(undefined)

const changeRequestOptionsLoading = ref<boolean>(false)

const selectedProject = ref<CampaignSelectInterface | null>(null)
const selectedAssignedTo = ref<{
  id: number
  display_name: string
} | null>(null)
const selectedSuppliers = ref<{
  id: number
  supplier_name: string
  division?: string
  division_display?: string
}[]>([])

const files = ref<FileWithReference[]>([])
const newFiles = ref<File[]>([])
const deletedFiles = ref<number[]>([])

const requestTypeChoices = ref<ChangeRequestType[]>([])
const projectTypeChoices = ref<ChangeRequestProjectType[]>([])
const servicesRequiredChoices = ref<ChangeRequestServicesRequired[]>([])
const statusChoices = ref<ChangeRequestStatus[]>([])
const priorityChoices = ref<ChangeRequestPriority[]>([])
const reasonForChangeChoices = ref<ChangeRequestReasonForChange[]>([])
const category2Choices = ref<ChangeRequestCategory2[]>([])


const selectedFileAttachmentId = ref<number | null>(null)
const showFileReviewDialog = ref<boolean>(false)

const showChangeRequestCommentsDialog = ref<boolean>(false)

const isShowCRInfo = computed(() => {
  // Check if the Action Required filter is active:
  // 1. If the Change Request (CR) has a valid status (Action Required), show CR Info
  // 2. If the Change Request only has two fields (ID and Code) and no valid status (No Action Required), hide CR Info
  if (props.actionRequiredFilter) {
    return !!originalChangeRequest.value?.status
  }

  // If the Action Required filter is inactive, always show CR Info
  return true
})

const hasChangeRequestData = computed(() => {
  return !!(originalChangeRequest.value && Object.keys(originalChangeRequest.value).length)
})

const isOtherCrProjectType = computed(() => {
  // 4 - Other
  return changeRequest.value?.project_type === 4
})

const projectTypeBasedOnCrRequestType = computed<number | undefined>(() => {
  const REQUEST_PROJECT_TYPE_TO_PROJECT_TYPE: Record<number, number | undefined> = {
    1: 2, // 1 - Dressing -> 2 - Branding Project
    2: 1, // 2 - Wayfinding -> 1 - Wayfinding Project
    3: 5, // 3 - KOP -> 5 - KOP Project
    5: 0, // 5 - Retail -> 0 - Retail Project
    // 4 - Other -> undefined (all types list)
  }

  const requestType = changeRequest.value?.project_type
  return requestType ? REQUEST_PROJECT_TYPE_TO_PROJECT_TYPE[requestType] : undefined
})

const getSelectedSupplierIds = () => selectedSuppliers.value?.map(s => s.id) || []

const hasSelectedSuppliersChanged = (selectedSupplierIds = getSelectedSupplierIds()) => {
  return originalSupplierIds.value.length !== selectedSupplierIds.length ||
    originalSupplierIds.value.some((id, index) => id !== selectedSupplierIds[index]) ||
    selectedSupplierIds.some((id, index) => id !== originalSupplierIds.value[index])
}

const getCrViewMode = async () => {
  const loadVersion = ++recordLoadVersion
  const recordId = props.changeRequestId
  originalChangeRequest.value = {}
  emit('accessLoaded', false)
  changeRequestLoading.value = true

  try {
    const res = await $getChangeRequest(String(props.changeRequestId), {
      action_required: props.actionRequiredFilter || undefined
    })
    if (loadVersion !== recordLoadVersion || recordId !== props.changeRequestId) return
    originalChangeRequest.value = res.data
    emit('accessLoaded', res.data.has_ordinary_access === true)
  } catch (e: any) {
    console.log(e)
  } finally {
    if (loadVersion === recordLoadVersion) changeRequestLoading.value = false
  }
}

const getCrEditMode = async () => {
  const loadVersion = ++recordLoadVersion
  const recordId = props.changeRequestId
  emit('accessLoaded', false)
  changeRequestLoading.value = true

  try {
    const res = await $getChangeRequest(String(props.changeRequestId), {})
    if (loadVersion !== recordLoadVersion || recordId !== props.changeRequestId) return
    originalChangeRequest.value = res.data
    emit('accessLoaded', res.data.has_ordinary_access === true)
    if (res.data.has_ordinary_access !== true) {
      isCrEditMode.value = false
      return
    }
    const { services_required_display, ...rest } = res.data
    changeRequest.value = {
      ...rest,
      services_required: services_required_display
    }
    originalPriority.value = changeRequest.value.priority ?? null
    originalStatus.value = changeRequest.value?.status ?? null

    selectedProject.value = changeRequest.value.project
      ? {
        id: changeRequest.value.project,
        name: changeRequest.value?.project_name || '',
        event_group: changeRequest.value?.event_group || null,
        event_group_name: changeRequest.value?.event_group_name || ''
      }
      : null

    selectedSuppliers.value = changeRequest.value?.suppliers?.length
      ? changeRequest.value?.suppliers_info?.map(s => ({ id: s.id, supplier_name: s?.supplier_name || '' })) || []
      : []
    originalSupplierIds.value = changeRequest.value?.suppliers || []

    files.value = changeRequest.value?.file_attachments?.length ?
      changeRequest.value.file_attachments.map(i => ({
        ...i,
        name: i.file ? i.file.split('/').pop() : '',
      })) : []

    // Wait for project changes due to project update (which triggers clearing of selectedAssignedTo)
    await nextTick(() => {
      selectedAssignedTo.value = changeRequest.value.assigned_to
        ? { display_name: changeRequest.value.assigned_to_name || '', id: changeRequest.value.assigned_to }
        : null
    })

    // If any approvals or subtasks are not marked as "complete" - disabled "Request Completed/Closed" status
    if (!changeRequest.value.is_all_approvals_and_subtasks_completed) {
      statusChoices.value = statusChoices.value.map((i: any) => ({
        ...i,
        disabled: i.value === 3,
        subtitle: i.value === 3 ? 'Some approvals or subtasks are not marked as "Complete"' : ''
      }))
    }
  } catch (e: any) {
    console.log(e)
  } finally {
    if (loadVersion === recordLoadVersion) changeRequestLoading.value = false
  }
}

const getChangeRequestOptions = async () => {
  changeRequestOptionsLoading.value = true
  try {
    const res = await $getChangeRequestOptions()
    requestTypeChoices.value = res.data.actions.POST.request_type.choices
    // TODO: Uncomment after Retail integration
    // projectTypeChoices.value = res.data.actions.POST.project_type.choices
    projectTypeChoices.value = res.data.actions.POST.project_type.choices.filter((i: any) => i.value !== 5)
    servicesRequiredChoices.value = res.data.actions.POST.services_required.choices
    statusChoices.value = res.data.actions.POST.status.choices
    priorityChoices.value = res.data.actions.POST.priority.choices
    reasonForChangeChoices.value = res.data.actions.POST.reason_for_change.choices
    category2Choices.value = res.data.actions.POST.category_2.choices
  } catch (e) {
    console.log(e)
  } finally {
    changeRequestOptionsLoading.value = false
  }
}

const updateChangeRequest = async (applyPriorityToChildren?: boolean, applySupplierToChildren?: boolean) => {
  if (!hasOrdinaryAccess.value || changeRequest.value.has_ordinary_access !== true) return
  changeRequestLoading.value = true

  const formData = new FormData()

  if (files.value) {
    // Add existing file updates
    const existingUpdates = files.value.filter(file => !!file.id).map(file => ({
      id: file.id,
      client_reference: file.client_reference || ''
    }))
    if (existingUpdates.length) formData.append('existing_files_updates', JSON.stringify(existingUpdates))

    // Add new files
    files.value.filter(file => !file.id).forEach((fileObj, fileObjIdx) => {
      if (fileObj?.file) {
        formData.append(`new_files`, fileObj.file)
        formData.append(`new_files_client_references`, fileObj?.client_reference || '')
      }
    })
  }

  if (deletedFiles.value.length) {
    for (let i = 0; i < deletedFiles.value.length; i++) {
      formData.append('delete_files', String(deletedFiles.value[i]))
    }
  }

  const changeRequestData = JSON.parse(JSON.stringify(changeRequest.value))

  // Remove suppliers key if it exists
  if ('suppliers' in changeRequestData) {
    delete changeRequestData.suppliers
  }

  changeRequestData.project = selectedProject.value ? selectedProject.value.id : ''
  changeRequestData.assigned_to = selectedAssignedTo.value ? selectedAssignedTo.value?.id : ''

  if (changeRequestData.reason_for_change !== 9) {
    changeRequestData.reason_for_change_text = ''
  }

  if (Array.isArray(selectedSuppliers.value)) {
    selectedSuppliers.value.map(i => i.id).forEach((item: any, index) => {
      formData.append('suppliers', item)
    })
  }

  // Add ChangeRequest fields to FormData
  for (const key in changeRequestData) {
    const value = changeRequestData[key as keyof ChangeRequest]
    if (key === 'services_required') {
      formData.append('services_required_data', JSON.stringify(value))
    }
    else if (key === 'due_date') {
      formData.append('due_date', value ? value as string : '')
    }
    else if (key === 'category_2') {
      formData.append('category_2', value ? value as string : '')
    }
    else if (value !== null) {
      if (Array.isArray(value)) {
        value.forEach((item: any, index) => {
          formData.append(`${key}`, item)
        })
      } else {
        formData.append(key, value as string)
      }
    }
  }

  if (applyPriorityToChildren !== undefined) {
    formData.append('apply_priority_to_children', applyPriorityToChildren ? 'true' : 'false')
  }
  if (applySupplierToChildren !== undefined) {
    formData.append('apply_supplier_to_children', applySupplierToChildren ? 'true' : 'false')
  }

  try {
    const res = await $updateChangeRequest(String(props.changeRequestId), formData)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Change Request updated successfully!'
    })
    originalPriority.value = changeRequest.value.priority ?? null
    originalStatus.value = changeRequest.value?.status ?? null
    originalSupplierIds.value = selectedSuppliers.value?.map(s => s.id) || []

    isCrEditMode.value = false
    refreshChangeRequest()

    emit('onChangeRequestUpdate')
  } catch (e: any) {
    // Log any errors that occur during the request
    const mes = e.response.data ?
      (e.response.data.detail ? e.response.data.detail : JSON.stringify(e.response.data)) :
      e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    changeRequestLoading.value = false
  }
}

const submitChangeRequestUpdate = async (skipCancelledStatusConfirm = false) => {
  const statusChangedToCancelled =
    !skipCancelledStatusConfirm &&
    originalStatus.value !== changeRequest.value.status &&
    changeRequest.value.status === CR_CANCELLED_STATUS_VALUE

  if (statusChangedToCancelled) {
    showCancelledStatusCascadeDialog.value = true
    return
  }

  const priorityChanged = originalPriority.value !== null
    && changeRequest.value.priority !== undefined
    && originalPriority.value !== changeRequest.value.priority
  if (priorityChanged) {
    showPriorityCascadeDialog.value = true
    return
  }

  const selectedSupplierIds = getSelectedSupplierIds()
  const suppliersChanged = hasSelectedSuppliersChanged(selectedSupplierIds)
  if (suppliersChanged && selectedSupplierIds.length <= 1) {
    showSupplierCascadeDialog.value = true
    return
  }

  await updateChangeRequest()
}

const onSubmit = async () => {
  // Validate the user form
  const validation = await changeRequestForm.value.validate()
  const isValid = validation.valid
  if (!isValid) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, Check all required fields'
    })
    return
  }

  if (props.changeRequestId) {
    pendingApplyPriorityToChildren.value = undefined
    await submitChangeRequestUpdate()
  }
}

const onPriorityCascadeDecision = async (applyToChildren: boolean) => {
  showPriorityCascadeDialog.value = false
  const selectedSupplierIds = getSelectedSupplierIds()
  const suppliersChanged = hasSelectedSuppliersChanged(selectedSupplierIds)
  if (suppliersChanged && selectedSupplierIds.length <=1) {
    pendingApplyPriorityToChildren.value = applyToChildren
    showSupplierCascadeDialog.value = true
    return
  }
  pendingApplyPriorityToChildren.value = undefined
  await updateChangeRequest(applyToChildren)
}

const onSupplierCascadeDecision = async (applyToChildren: boolean) => {
  showSupplierCascadeDialog.value = false
  const applyPriorityToChildren = pendingApplyPriorityToChildren.value
  pendingApplyPriorityToChildren.value = undefined
  await updateChangeRequest(applyPriorityToChildren, applyToChildren)
}

const onCancelledStatusConfirm = async () => {
  showCancelledStatusCascadeDialog.value = false
  await submitChangeRequestUpdate(true)
}

const handleFileSelection = (selected: File[] | null) => {
  if (!selected || selected.length === 0) return

  const newFileObjects: FileWithReference[] = Array.from(selected).map(file => ({
    file,
    name: file.name,
    client_reference: ''
  }))

  files.value = [...files.value, ...newFileObjects]
  newFiles.value = [] // Reset the file input
}

const removeFile = (index: number, file: FileWithReference) => {
  files.value.splice(index, 1)

  // If file already exist - mark as deleted
  if (!!file?.id) {
    deletedFiles.value.push(file?.id) // Track the deleted file ID
  }
}

const refreshChangeRequest = () => {
  if (props.changeRequestId) {
    if (isCrEditMode.value) {
      getCrEditMode()
      getChangeRequestOptions()
    } else {
      getCrViewMode()
    }
  }
}

watch(() => props.changeRequestId, () => {
  ++recordLoadVersion
  originalChangeRequest.value = {}
  changeRequest.value = {}
  changeRequestLoading.value = false
  emit('accessLoaded', false)
  isCrEditMode.value = false
  refreshChangeRequest()
}, { immediate: true })

watch(() => props.actionRequiredFilter, () => {
  ++recordLoadVersion
  originalChangeRequest.value = {}
  emit('accessLoaded', false)
  isCrEditMode.value = false
  refreshChangeRequest()
})

onMounted(() => {
  isMounted.value = true
})

const fileAttachments = computed(() => originalChangeRequest.value.file_attachments || [])

const handleFileClick = (file: any) => {
  selectedFileAttachmentId.value = file?.id || null
  showFileReviewDialog.value = true
}
</script>

<template>
  <div>
    <ConfirmDialog
      v-model="showPriorityCascadeDialog"
      title="Apply Priority to Subtasks and Approvals?"
      text="Do you want to apply this priority to all related subtasks and approvals?"
      cancel-btn-name="No"
      submit-btn-name="Yes"
      @onCancel="onPriorityCascadeDecision(false)"
      @onSubmit="onPriorityCascadeDecision(true)"
    />
    <ConfirmDialog
      v-model="showSupplierCascadeDialog"
      title="Apply Supplier to Subtasks and Approvals?"
      text="Do you want to apply this supplier to all related subtasks and approvals?"
      cancel-btn-name="No"
      submit-btn-name="Yes"
      @onCancel="onSupplierCascadeDecision(false)"
      @onSubmit="onSupplierCascadeDecision(true)"
    />
    <ConfirmDialog
      v-model="showCancelledStatusCascadeDialog"
      title="Change Status to Cancelled?"
      text="Are you sure you want to change the status to “Cancelled”? This change will be applied to all related subtasks and approvals."
      cancel-btn-name="No"
      submit-btn-name="Yes"
      @onSubmit="onCancelledStatusConfirm"
    />
    <ChangeRequestObjectCommentsDialog
      v-model="showChangeRequestCommentsDialog"
      :change-request-id="originalChangeRequest?.id"
      @onAddComment="getCrViewMode"
    />
    <CrFileReviewDialog
      v-model="showFileReviewDialog"
      :cr-item-type="'cr'"
      :file-attachment-id="selectedFileAttachmentId"
    />
    <VCard v-if="changeRequestLoading || originalChangeRequest?.id || hasChangeRequestData">
      <template #title>
        <div class="d-flex align-center">
          Change Request
          {{ originalChangeRequest?.code }}
          <VBtn
            v-if="showChangeRequestExternalLink"
            size=""
            :disabled="!changeRequestId"
            class="ml-2 px-2 py-1"
            :to="{ path: `/change-requests/${changeRequestId}` }"
          >
            <VIcon icon="tabler-external-link" />
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                Open Full Change Request Page
              </span>
            </VMenu>
          </VBtn>
        </div>
      </template>
      <template #append>
        <VBtn
          v-if="isShowCRInfo && !isCrEditMode && hasOrdinaryAccess && !changeRequestLoading"
          :disabled="!originalChangeRequest?.id"
          @click="isCrEditMode = true; refreshChangeRequest()"
        >
          Edit
        </VBtn>
      </template>
      <VCardText>
        <VOverlay
          v-model="changeRequestLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <VAlert
          v-if="!changeRequestLoading && !hasChangeRequestData"
          type="warning"
          variant="tonal"
        >
          Change Request data not available.
        </VAlert>
        <div v-else-if="isShowCRInfo">
          <VForm
            ref="changeRequestForm"
            @submit.prevent="onSubmit"
          >
            <VContainer fluid>
              <VOverlay
                v-model="changeRequestLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <VRow>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Name *' : 'Name:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextField
                      v-model="changeRequest.name"
                      name="Name"
                      :rules="[requiredValidator]"
                      type="text"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Change Request Type *' : 'Change Request Type:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.request_type"
                      :items="[...requestTypeChoices]"
                      :rules="[requiredValidator]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      name="Change Request Type"
                      item-value="value"
                      item-title="display_name"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.request_type_display || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Type of Project *' : 'Type of Project:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.project_type"
                      :items="[...projectTypeChoices]"
                      :rules="[requiredValidator]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      name="Type of Project"
                      item-value="value"
                      item-title="display_name"
                      @update:modelValue="selectedProject = null; selectedSuppliers = []"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.project_type_display || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Quantity *' : 'Quantity:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextField
                      v-model="changeRequest.qty"
                      name="Quantity"
                      :rules="[requiredValidator]"
                      type="number"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.qty || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? (isOtherCrProjectType ? 'Project' : 'Project *') : 'Project:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <CampaignSelect
                      v-model="selectedProject"
                      :disabled="changeRequestLoading || !changeRequest?.project_type"
                      name="Project"
                      :rules="isOtherCrProjectType ? [] : [requiredValidator]"
                      :hide-details="isOtherCrProjectType ? true : undefined"
                      :type="projectTypeBasedOnCrRequestType"
                      :label="''"
                      :is-with-event-groups="true"
                      @update:modelValue="selectedSuppliers = []"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.project_name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Program' : 'Program:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      :model-value="{
                        id: selectedProject?.event_group || null,
                        name: selectedProject?.event_group_name || ''
                      }"
                      disabled
                      item-value="id"
                      item-title="name"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.event_group_name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Category 2' : 'Category 2:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.category_2"
                      :items="[...category2Choices]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      name="Category 2"
                      item-value="value"
                      item-title="display_name"
                      clearable
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.category_2_display || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Sales Order' : 'Sales Order:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextField
                      v-model="changeRequest.sales_order_name"
                      name="Sales Order"
                      type="text"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.sales_order_name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Item Code (if known)' : 'Item Code (if known):' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextField
                      v-model="changeRequest.item_code"
                      name="Item Code"
                      type="text"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.item_code || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Services Required' : 'Services Required:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.services_required"
                      :items="[...servicesRequiredChoices]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      multiple
                      item-value="value"
                      item-title="display_name"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.services_required_display?.map(i => i.charAt(0).toUpperCase() + i.slice(1)).join(', ') }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Assigned To' : 'Assigned To:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <UserSelect
                      v-model="selectedAssignedTo"
                      :label="''"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.assigned_to_name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Status' : 'Status:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.status"
                      :items="[...statusChoices]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      :item-props="(item) => {
                        return {
                          value: item.value,
                          title: item.display_name,
                          subtitle: item.subtitle,
                          disabled: item.disabled,
                        }
                      }"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.status_display || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Priority' : 'Priority:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.priority"
                      :items="[...priorityChoices]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      item-title="display_name"
                      item-value="value"
                    />
                  </div>
                  <div v-else>
                    <VChip
                      :color="getPriorityColor(originalChangeRequest?.priority)"
                    >
                      {{ originalChangeRequest?.priority_display || '' }}
                    </VChip>
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Reason for Change *' : 'Reason for Change:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VSelect
                      v-model="changeRequest.reason_for_change"
                      :items="[...reasonForChangeChoices]"
                      :disabled="!!changeRequestOptionsLoading"
                      :loading="!!changeRequestOptionsLoading"
                      :rules="[requiredValidator]"
                      name="Reason for Change"
                      item-value="value"
                      item-title="display_name"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.reason_for_change_display || '' }}
                  </div>
                </VCol>
                <!-- Reason for Change "Other" option custom input -->
                <VCol
                  v-if="isCrEditMode && changeRequest?.reason_for_change === 9"
                  cols="12"
                >
                  <label class="label-text font-weight-bold">Reason for Change (Custom) *</label>
                  <VTextField
                    v-model="changeRequest.reason_for_change_text"
                    name="Reason for Change (Custom)"
                    :rules="[requiredValidator]"
                    type="text"
                  />
                </VCol>
                <!-- /Reason for Change "Other" option custom input -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Due Date' : 'Due Date:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <AppDateTimePicker
                      v-model="changeRequest.due_date"
                      :config="{ dateFormat: 'Y-m-d' }"
                      clearable
                    />
                  </div>
                  <div v-else>
                    <div :class="{ 'text-primary': isDateOverdue(changeRequest.due_date) }">
                      {{ originalChangeRequest?.due_date || '' }}
                    </div>
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Suppliers' : 'Suppliers:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <MultipleSuppliersSelect
                      v-model="selectedSuppliers"
                      :disabled="!selectedProject?.id && !isOtherCrProjectType"
                      :label="''"
                      :project-suppliers-only="!selectedProject?.id && isOtherCrProjectType ? undefined : true"
                      :project-id="selectedProject?.id"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.suppliers_display_name || '' }}
                  </div>
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Details' : 'Details:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextarea
                      v-model="changeRequest.details"
                      name="Details"
                      type="text"
                    />
                  </div>
                  <div v-else>
                  <div
                    v-if="originalChangeRequest?.details"
                    class="border rounded px-2 py-1"
                    style="background-color: rgba(var(--v-theme-on-surface), 0.05) !important;"
                  >
                    <p
                      v-for="(line, index) in originalChangeRequest.details.split('\n')"
                      :key="index"
                      class="mb-2"
                    >
                      {{ line }}
                    </p>
                  </div>
                  <div v-else>
                    {{ '' }}
                  </div>
                  </div>

                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <label class="label-text font-weight-bold">
                    {{ isCrEditMode ? 'Client Reference' : 'Client Reference:' }}
                  </label>
                  <div v-if="isCrEditMode">
                    <VTextarea
                      v-model="changeRequest.client_reference"
                      name="Client Reference"
                      type="text"
                    />
                  </div>
                  <div v-else>
                    {{ originalChangeRequest?.client_reference || '' }}
                  </div>
                </VCol>
                <VCol
                  v-if="!isCrEditMode"
                  cols="12"
                >
                  <label class="label-text font-weight-bold">Notes: </label>
                  <div>
                    <VChip
                    size="small"
                    :color="originalChangeRequest?.comments_count ? 'primary' : 'default'"
                    variant="outlined"
                  >
                    {{ originalChangeRequest?.comments_count || 0 }}
                  </VChip>

                  <VBtn
                    v-if="originalChangeRequest?.id"
                    size=""
                    class="pa-1 ml-1"
                    @click="showChangeRequestCommentsDialog = true"
                  >
                    <VIcon icon="tabler-message" />
                    <VMenu
                      activator="parent"
                      location="top center"
                      open-on-hover
                      open-delay="100"
                      close-delay="0"
                      offset="5"
                      class="default-tooltip"
                    >
                      <span class="px-2">
                        View/Add Notes
                      </span>
                    </VMenu>
                  </VBtn>
                  </div>
                </VCol>
              </VRow>
              <VRow>
                <VCol
                  cols="12"
                >
                  <label class="label-text font-weight-bold">File Attachments</label>
                  <div v-if="isCrEditMode">
                    <VFileInput
                      v-model="newFiles"
                      multiple
                      label="Select file..."
                      @update:modelValue="handleFileSelection"
                    />

                    <VRow v-if="files.length > 0" class="mt-3">
                      <VCol
                        v-for="(file, index) in files"
                        :key="index"
                        cols="12"
                        class="py-1"
                      >
                        <VCard class="pa-1 border">
                          <div class="d-flex align-center">
                            <VIcon class="mr-2">mdi-file</VIcon>
                            <span class="text-truncate" style="max-width: 300px">{{ file.name }}</span>
                            <VBtn
                              v-if="file.id && file.file"
                              size="sm"
                              class="pa-1 ml-2"
                              :href="file.file"
                              target="_blank"
                            >
                              <VIcon size="">mdi-eye</VIcon>
                            </VBtn>
                            <VSpacer />
                            <VBtn
                              icon="mdi-close"
                              variant="text"
                              size="small"
                              @click="removeFile(index, file)"
                            />
                          </div>
                        </VCard>
                      </VCol>
                    </VRow>
                  </div>
                  <div v-else>
                    <div v-if="changeRequestId" class="file-container mt-1">
                      <div v-for="file in fileAttachments" :key="file.id" class="file-item">
                        <a
                          v-if="file.file"
                          :href="file.file"
                          target="_blank"
                          class="cursor-pointer d-inline-flex align-center"
                          @click.prevent="handleFileClick(file)"
                        >
                          <VIcon
                            :icon="getFileIcon(file.file)"
                            class="mr-1 text-primary"
                          />
                          {{ getCleanFilenameFromUrl(file.file) }}
                        </a>
                      </div>
                    </div>
                  </div>
                </VCol>
              </VRow>
              <VRow v-if="isCrEditMode">
                <VCol class="d-flex justify-space-between flex-wrap">
                  <VBtn
                    class="ma-1"
                    color="secondary"
                    @click="isCrEditMode = false; refreshChangeRequest()"
                  >
                    Cancel
                  </VBtn>
                  <VBtn class="ma-1" type="submit">
                    Update Change Request
                  </VBtn>
                </VCol>
              </VRow>
            </VContainer>
          </VForm>
        </div>
      </VCardText>
    </VCard>
    <VCard v-else>
      <template #title>
        Change Request not found, or you do not have access to it
      </template>
    </VCard>
  </div>
</template>

<style lang="scss">
.file-container {
  position: relative;
  display: flex;
  flex-direction: column;
}

.file-item {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1px 0;
  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.05) !important;
  }
}
</style>
